import { useState } from 'react'
import type { FacilitatorLiveSessionCard } from '../../../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import { useEndSprintActivityForTeam } from '../../../../apiLayer/queryHooks/mutations'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { ControlTowerActionOutcome } from './controlTowerHelpers'

/** Confirm-then-end flow for one team's activity: the card asks first, the outcome stays on it until dismissed. */
export const useEndActivityForTeam = () => {
  const { t } = useTranslation()
  const endForTeam = useEndSprintActivityForTeam()
  const [cardToEnd, setCardToEnd] = useState<FacilitatorLiveSessionCard | null>(null)
  const [outcome, setOutcome] = useState<ControlTowerActionOutcome | null>(null)

  const describeSuccess = (card: FacilitatorLiveSessionCard, alreadyCompleted: boolean): string => {
    const names = { activity: card.activityTitle, team: card.teamName }
    return alreadyCompleted
      ? t(
          'facilitator.controlTower.endActivityForTeamAlreadyDone',
          '{{team}} had already completed {{activity}}.',
          names
        )
      : t('facilitator.controlTower.endActivityForTeamDone', '{{activity}} ended for {{team}}.', names)
  }

  const confirm = () => {
    if (!cardToEnd) return
    const card = cardToEnd
    endForTeam.mutate(
      { sprintId: card.sprintId, activityId: card.activityId, teamId: card.teamId },
      {
        onSuccess: (response) => {
          setCardToEnd(null)
          if (!response.success) {
            setOutcome({
              severity: 'error',
              message: t('facilitator.controlTower.endActivityForTeamFailed', 'Could not end the activity.')
            })
            return
          }
          setOutcome({ severity: 'success', message: describeSuccess(card, response.data.alreadyCompleted) })
        },
        onError: () => {
          setCardToEnd(null)
          setOutcome({
            severity: 'error',
            message: t('facilitator.controlTower.endActivityForTeamFailed', 'Could not end the activity.')
          })
        }
      }
    )
  }

  return {
    cardToEnd,
    openDialog: setCardToEnd,
    cancelDialog: () => setCardToEnd(null),
    confirm,
    isEnding: endForTeam.isPending,
    outcome,
    clearOutcome: () => setOutcome(null)
  }
}
